/**
 * Circuit breaker pattern for resilient HTTP requests.
 *
 * States: closed (normal) -> open (failing) -> half-open (testing) -> closed
 */

export type CircuitBreakerState = 'closed' | 'open' | 'half-open';

export interface CircuitBreakerConfig {
  /** Number of consecutive failures before opening the circuit (default: 5) */
  failureThreshold?: number;
  /** Time in ms to wait before transitioning from open to half-open (default: 30000) */
  resetTimeout?: number;
  /** Number of successes in half-open state before closing (default: 1) */
  successThreshold?: number;
}

export interface CircuitBreakerStatus {
  state: CircuitBreakerState;
  failures: number;
  successes: number;
  lastFailureTime: number | null;
  nextRetryTime: number | null;
}

export class CircuitBreaker {
  private state: CircuitBreakerState = 'closed';
  private failures = 0;
  private successes = 0;
  private lastFailureTime: number | null = null;

  private readonly failureThreshold: number;
  private readonly resetTimeout: number;
  private readonly successThreshold: number;

  constructor(config: CircuitBreakerConfig = {}) {
    this.failureThreshold = config.failureThreshold ?? 5;
    this.resetTimeout = config.resetTimeout ?? 30000;
    this.successThreshold = config.successThreshold ?? 1;
  }

  /**
   * Check if a request can proceed through the circuit breaker.
   */
  canExecute(): boolean {
    if (this.state === 'closed') {
      return true;
    }

    if (this.state === 'open') {
      const now = Date.now();
      if (this.lastFailureTime && now - this.lastFailureTime >= this.resetTimeout) {
        this.state = 'half-open';
        this.successes = 0;
        return true;
      }
      return false;
    }

    // half-open: allow requests through for testing
    return true;
  }

  /**
   * Record a successful request.
   */
  onSuccess(): void {
    if (this.state === 'half-open') {
      this.successes++;
      if (this.successes >= this.successThreshold) {
        this.state = 'closed';
        this.failures = 0;
        this.successes = 0;
        this.lastFailureTime = null;
      }
    } else if (this.state === 'closed') {
      this.failures = 0;
    }
  }

  /**
   * Record a failed request.
   */
  onFailure(): void {
    this.failures++;
    this.lastFailureTime = Date.now();

    if (this.state === 'half-open') {
      this.state = 'open';
      this.successes = 0;
    } else if (this.state === 'closed' && this.failures >= this.failureThreshold) {
      this.state = 'open';
    }
  }

  /**
   * Get current circuit breaker status.
   */
  getStatus(): CircuitBreakerStatus {
    return {
      state: this.state,
      failures: this.failures,
      successes: this.successes,
      lastFailureTime: this.lastFailureTime,
      nextRetryTime:
        this.state === 'open' && this.lastFailureTime
          ? this.lastFailureTime + this.resetTimeout
          : null,
    };
  }

  /**
   * Reset the circuit breaker to its initial closed state.
   */
  reset(): void {
    this.state = 'closed';
    this.failures = 0;
    this.successes = 0;
    this.lastFailureTime = null;
  }
}
